/* eslint-disable max-len */
/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useLayoutEffect, useMemo, useState } from 'react';

import { ToDoOfferItem as ToDoOfferItemType } from 'client-code/recordings';

import { AppUtils } from '../../../../../../core/AppUtils';
import { Channel } from '../../../../../../core/entities';
import { ServicesClientImpl } from '../../../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../../../core/locale';
import { DateTime } from '../../../../../../core/utils';
import { Mousetrap } from '../../../../../common';
import { ProgramCell } from '../../../../../Guide/ProgramCell';
import { CellTypes } from '../../../../../Guide/ProgramCell/CellTypes';
import { getToDoListOfferItemIconClassname, getTodoOfferItemTitle } from '../../ToDoList.utils';
import { ToDoListHighlight } from '../../ToDoListHighlight/ToDoListHighlight';

import '../ToDoListContent.scss';

export type ToDoOfferItemProps = {
    item: ToDoOfferItemType;
    allChannels: Channel[];
    rowIndex: number;
};

const ToDoOfferItem = ({ item, allChannels, rowIndex }: ToDoOfferItemProps) => {
    // eslint-disable-next-line no-unused-vars
    const [_stateUpdater, setStateUpdater] = useState(0);
    const services = new ServicesClientImpl();
    const shouldHideMetadata = services.shouldHideMetadata();

    const channel = useMemo(() => {
        const ch = allChannels.find((c) => c.channelNumber === item.channelNumber);
        if (!ch) {
            return null;
        }

        return ch;
    }, [allChannels, item.channelNumber]);

    useLayoutEffect(() => {
        // The ProgramCell overflow mechanism relies on the component re-rendering so that its refs are correctly populated.
        // This component is very small and has no state, so it doesn't re-render until a keypress occurs.
        // I tried several ways to fix this issue inside ProgramCell, but each approach caused unexpected bugs in the main guide,
        // such as: overflow no longer working, the small left/right icons at the end of the table overlapping with text,
        // and recording icons overlapping with other content.
        // As a result, the only reliable solution was to force a re-render from the parent component.
        // This is not ideal, but it's currently the only workaround that ensures no regression.
        setStateUpdater((prev) => prev + 1);
    }, []);

    if (!channel) {
        return null;
    }

    return (
        <div className="to-do-list-offer-item">
            <Mousetrap value={AppUtils.createHighlightId(0, rowIndex)}>
                <ToDoListHighlight id={AppUtils.createHighlightId(0, rowIndex)} />
                <div className="to-do-list-row">
                    <div className="to-do-row-cell status-icon">
                        <div className={getToDoListOfferItemIconClassname(item)}></div>
                    </div>
                    <div className="to-do-row-cell start-time">
                        {new DateTime(item.startTime).to12HourClock()}
                    </div>
                    <div className="to-do-row-cell program-cell">
                        <ProgramCell
                            title={
                                shouldHideMetadata && item.isAdult
                                    ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)
                                    : getTodoOfferItemTitle(item)
                            }
                            type={CellTypes.DEFAULT}
                            isSubscribed={channel.isSubscribed}
                            duration={100}
                            movieYear={shouldHideMetadata && item.isAdult ? null : item.movieYear}
                            channel={channel}
                            isNew={item.isNew}
                        />
                    </div>
                    <div className="to-do-row-cell channel-number">{item.channelNumber}</div>
                    <div className="to-do-row-cell channel-quality">
                        <div
                            className={`preview-stream-type-icon ${item.videoResolution?.toLocaleLowerCase()}`}
                        ></div>
                    </div>
                </div>
            </Mousetrap>
        </div>
    );
};

export default ToDoOfferItem;
